import { HttpError, LogicalFilter, useInvalidate } from "@refinedev/core";
import React, { useState } from "react";

import { CreateButton, EditButton, List, useTable } from "@refinedev/antd";
import { Button, Card, Popconfirm, Space, Table, Tag, Tooltip, message } from "antd";
import Search from "antd/es/input/Search";
import dayjs from "dayjs";
import { IUser, IUserInviteInfo } from "interfaces";
import { USERS_RES_ID } from "utility/constants";
import useHttpClient from "components/hooks/useHttpClient";
import { InviteUserDialog } from "./inviteUserDialog";
import { getUserInfoFromCache } from "utility/helper";

const inviteStatusColor = (status?: string) => {
  switch (status) {
    case "Pending":
      return "processing";
    case "Accepted":
      return "success";
    case "Expired":
      return "warning";
    default:
      return "default";
  }
};

const inviteStatusLabel = (status?: string) => {
  switch (status) {
    case "Pending":
      return "Väntar";
    case "Accepted":
      return "Accepterad";
    case "Expired":
      return "Utgången";
    default:
      return "Ingen";
  }
};

const formatInviteTooltip = (invite?: IUserInviteInfo | null) => {
  if (!invite) {
    return "Ingen inbjudan";
  }

  const parts = [
    `Status: ${inviteStatusLabel(invite.status)}`,
    `Skapad: ${dayjs(invite.createdAt).format("YYYY-MM-DD HH:mm")}`,
    `Går ut: ${dayjs(invite.expiresAt).format("YYYY-MM-DD HH:mm")}`,
  ];

  if (invite.usedAt) {
    parts.push(`Använd: ${dayjs(invite.usedAt).format("YYYY-MM-DD HH:mm")}`);
  }

  if (invite.invitedByUserId) {
    parts.push(`Inbjuden av userId: ${invite.invitedByUserId}`);
  }

  return parts.join("\n");
};

export const UsersList: React.FC = () => {
  const { tableProps, setFilters } = useTable<IUser, HttpError>({
    resource: USERS_RES_ID,
    syncWithLocation: false,
    filters: {
      defaultBehavior: "replace",
    },
  });
  const [inviteOpen, setInviteOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { makeHttpRequestThrowErrors } = useHttpClient();
  const invalidate = useInvalidate();
  const currentTenantId = Number(getUserInfoFromCache()?.tenant);

  const refreshUsers = () => {
    invalidate({
      resource: USERS_RES_ID,
      invalidates: ["list"],
    });
  };

  const handleSearch = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      setFilters([]);
      return;
    }

    setFilters([
      { field: "search", operator: "contains", value: trimmed } as LogicalFilter,
    ]);
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await makeHttpRequestThrowErrors("DELETE", `users/${id}`);
      message.success("Användaren raderades");
      refreshUsers();
    } catch (error: any) {
      const apiMessage =
        error?.message ||
        error?.Message ||
        (typeof error === "string" ? error : null) ||
        "Kunde inte radera användaren";
      message.error(apiMessage);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Card bordered={false} className="criclebox h-full">
      <List
        title={"Användare"}
        headerButtons={
          <Space>
            <Search
              placeholder="Sök efter e-post..."
              allowClear
              onSearch={handleSearch}
              style={{ width: 280 }}
            />
            <Button onClick={() => setInviteOpen(true)}>Bjud in</Button>
            <CreateButton />
          </Space>
        }
      >
        <Table
          {...tableProps}
          rowKey="id"
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
        >
          <Table.Column<IUser> dataIndex="id" title="Id" />
          <Table.Column<IUser> dataIndex="username" title="Email" sorter />
          <Table.Column<IUser>
            title="Inbjudan"
            dataIndex="latestInvite"
            render={(_, record) => {
              const invite = record.latestInvite;
              const inviteCount = record.invites?.length ?? 0;
              return (
                <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{formatInviteTooltip(invite)}</span>}>
                  <Space size={4}>
                    <Tag color={inviteStatusColor(invite?.status)}>
                      {inviteStatusLabel(invite?.status)}
                    </Tag>
                    {inviteCount > 1 && (
                      <Tag>{inviteCount} st</Tag>
                    )}
                  </Space>
                </Tooltip>
              );
            }}
          />
          <Table.Column<IUser>
            title="Handling"
            align="center"
            dataIndex="actions"
            render={(_, record) => {
              const isSuperAdmin = record.roles?.includes("SuperAdmin");
              return (
                <Space>
                  <EditButton hideText size="small" recordItemId={record.id} />
                  <Popconfirm
                    title="Radera användare?"
                    description="Detta går inte att ångra."
                    okText="Radera"
                    cancelText="Avbryt"
                    disabled={isSuperAdmin}
                    onConfirm={() => handleDelete(record.id)}
                  >
                    <Button
                      danger
                      size="small"
                      disabled={isSuperAdmin}
                      loading={deletingId === record.id}
                    >
                      Radera
                    </Button>
                  </Popconfirm>
                </Space>
              );
            }}
          />
        </Table>
      </List>
      <InviteUserDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSuccess={refreshUsers}
        defaultTenantId={Number.isNaN(currentTenantId) ? null : currentTenantId}
      />
    </Card>
  );
};
